<template>
  <div class="custom-page-form">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">{{ isEdit ? "Edit custom page" : "Create custom page" }}</h4>
      <div class="d-flex align-items-center">
        <a
          v-if="isEdit && publicUrl"
          :href="publicUrl"
          target="_blank"
          rel="noopener"
          class="btn btn-default btn-sm mr-2"
        >Open on site</a>
        <router-link :to="{ name: 'custom-pages-list' }" class="btn btn-default btn-sm">Back to list</router-link>
      </div>
    </div>

    <div v-loading="loading" class="custom-page-form__layout">
      <div class="custom-page-form__main">
        <card title="Address">
          <div class="row mb-2">
            <div class="col-md-3">
              <label>Language *</label>
              <el-select v-model="form.lang" :disabled="isEdit" placeholder="Lang" style="width: 100%">
                <el-option v-for="l in langs" :key="l" :label="l.toUpperCase()" :value="l" />
              </el-select>
            </div>
            <div class="col-md-9">
              <label>URL *</label>
              <el-input
                v-model="form.alias"
                :disabled="isEdit"
                placeholder="/clinics/country=turkey/procedure=rhinoplasty-nose-job/cost/"
              />
            </div>
          </div>
          <p class="small text-muted mb-0">
            The full path on the site, with both slashes. The page replaces whatever this address
            renders today. Language and URL are the page key — to move a page, create it anew and
            delete the old one.
          </p>
        </card>

        <card title="SEO">
          <div class="mb-3">
            <label>Title</label>
            <el-input v-model="form.title" placeholder="Shown in the browser tab and in search results" />
          </div>
          <div class="mb-3">
            <label>Description</label>
            <el-input v-model="form.description" type="textarea" :rows="3" placeholder="Meta description" />
          </div>
          <div>
            <label>H1</label>
            <el-input v-model="form.h1" placeholder="The heading on the page itself" />
          </div>
        </card>

        <card title="Hero block">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <span>This page has a hero block</span>
            <el-switch v-model="form.hasHero" active-color="#a3cc0e" inactive-color="#f96332" />
          </div>

          <template v-if="form.hasHero">
            <div class="mb-3">
              <label>Lead *</label>
              <el-input v-model="form.hero.lead" type="textarea" :rows="3" placeholder="The intro paragraph under H1" />
              <p class="small mb-0" :class="form.hero.lead.trim() ? 'text-muted' : 'text-danger'">
                Required — the whole hero block is dropped on save if this is left empty.
              </p>
            </div>

            <div class="mb-3">
              <label>Updated at</label>
              <el-input v-model="form.hero.updatedAt" placeholder="e.g. Updated September 2026" />
            </div>

            <div class="mb-3">
              <label>Call to action</label>
              <div class="row">
                <div class="col-md-5 mb-2">
                  <el-input v-model="form.hero.cta.label" placeholder="Button label" />
                </div>
                <div class="col-md-7 mb-2">
                  <el-input v-model="form.hero.cta.url" placeholder="https://…" />
                </div>
              </div>
              <p class="small text-muted mb-0">Both label and URL are required — either one alone is dropped.</p>
            </div>

            <div class="mb-3">
              <label>Advertiser disclosure</label>
              <el-input v-model="form.hero.disclosure.label" placeholder="Short caption, e.g. Advertising Disclosure" class="mb-2" />
              <el-input v-model="form.hero.disclosure.text" type="textarea" :rows="2" placeholder="Full text shown when the caption is opened" class="mb-2" />
              <el-input v-model="form.hero.disclosure.url" placeholder="Link to the policy page" />
              <p class="small text-muted mb-0 mt-1">Text is required for the disclosure to be kept — label and URL are optional.</p>
            </div>

            <div>
              <label>Authors</label>
              <div v-for="(author, index) in form.hero.authors" :key="index" class="hero-author-row mb-2 pb-2">
                <div class="row align-items-center">
                  <div class="col-md-2 mb-2">
                    <el-select v-model="author.kind" style="width: 100%">
                      <el-option label="Author" value="author" />
                      <el-option label="Reviewer" value="reviewer" />
                    </el-select>
                  </div>
                  <div class="col-md-3 mb-2"><el-input v-model="author.name" placeholder="Name" /></div>
                  <div class="col-md-3 mb-2"><el-input v-model="author.role" placeholder="Role" /></div>
                  <div class="col-md-2 mb-2"><el-input v-model="author.avatarUrl" placeholder="Avatar URL" /></div>
                  <div class="col-md-1 mb-2"><el-input v-model="author.profileUrl" placeholder="Profile URL" /></div>
                  <div class="col-md-1 mb-2 text-right">
                    <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeAuthor(index)" />
                  </div>
                </div>
              </div>
              <el-button size="mini" @click="addAuthor">Add author</el-button>
              <p class="small text-muted mt-2 mb-0">A name is required for an author row to be kept.</p>
            </div>
          </template>
        </card>

        <card title="Content blocks">
          <div v-if="!form.contentBlocks.length" class="small text-muted mb-3">No content blocks yet.</div>

          <div v-for="(block, index) in form.contentBlocks" :key="block._key" class="content-block-item mb-4 pb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong>Block {{ index + 1 }}{{ block.heading ? ` — ${block.heading}` : "" }}</strong>
              <div>
                <el-button size="mini" icon="el-icon-arrow-up" :disabled="index === 0" @click="moveContentBlock(index, -1)" />
                <el-button size="mini" icon="el-icon-arrow-down" :disabled="index === form.contentBlocks.length - 1" @click="moveContentBlock(index, 1)" />
                <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeContentBlock(index)" />
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-md-4 mb-2">
                <label>Heading</label>
                <el-input v-model="block.heading" placeholder="Printed as this section's h2" />
              </div>
              <div class="col-md-4 mb-2">
                <label>Anchor</label>
                <el-input v-model="block.anchor" placeholder="e.g. cities" />
              </div>
              <div class="col-md-4 mb-2">
                <label>Nav title</label>
                <el-input v-model="block.navTitle" placeholder="TOC label — defaults to heading" />
              </div>
            </div>
            <p class="small text-muted mb-2">
              Anchor: lowercase letters/digits/hyphens only, must be unique on the page. Leave empty
              to keep this block out of the table of contents.
            </p>
            <div class="custom-page-form__editor-wrap">
              <ckeditor
                :key="`block-${block._key}-${editorRenderKey}`"
                v-model="block.content"
                :config="block.ckeditorConfig"
              ></ckeditor>
            </div>
          </div>

          <el-button size="mini" @click="addContentBlock">Add content block</el-button>
        </card>

        <card v-if="form.customBlocks.length" title="Other blocks">
          <p class="small text-muted mb-3">
            Block types this screen has no dedicated editor for — shown as raw fields so nothing
            here is locked to API-only edits.
          </p>
          <div v-for="(block, index) in form.customBlocks" :key="block._key" class="content-block-item mb-4 pb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong>{{ block.type }} block</strong>
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeCustomBlock(index)" />
            </div>
            <div class="row mb-2">
              <div class="col-md-4 mb-2"><label>Heading</label><el-input v-model="block.heading" /></div>
              <div class="col-md-4 mb-2"><label>Anchor</label><el-input v-model="block.anchor" /></div>
              <div class="col-md-4 mb-2"><label>Nav title</label><el-input v-model="block.navTitle" /></div>
            </div>
            <key-value-editor v-model="block.data" />
          </div>
        </card>
      </div>

      <div class="custom-page-form__sidebar">
        <card title="Header image">
          <div class="text-center mb-2">
            <img v-if="imagePreview" :src="imagePreview" class="custom-page-form__img mb-2" />
            <p v-else class="small text-muted mb-2">No image</p>
            <div>
              <button type="button" class="custom-page-form__upload-btn" @click.prevent="$refs.imageFile.click()">
                Choose image
              </button>
            </div>
            <input
              ref="imageFile"
              type="file"
              accept="image/jpeg,image/png"
              style="display: none"
              @change="handleFileUpload"
            />
            <small v-if="imageFile" class="text-success d-block mt-1">{{ imageFile.name }}</small>
          </div>
          <label>Alt text</label>
          <el-input v-model="form.imageAlt" placeholder="What is on the image" />
          <p class="small text-muted mt-2 mb-0">
            Uploaded to S3 and resized to 1200×675 and 600×338 when the page is saved.
          </p>
        </card>

        <card title="Other languages">
          <div v-for="(translation, index) in form.translations" :key="index" class="d-flex align-items-center mb-2">
            <el-select v-model="translation.lang" placeholder="Lang" style="width: 90px" class="mr-2">
              <el-option v-for="l in langs" :key="l" :label="l.toUpperCase()" :value="l" />
            </el-select>
            <el-input v-model="translation.alias" placeholder="/kliniken/…/kosten/" class="mr-2" />
            <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeTranslation(index)" />
          </div>
          <el-button size="mini" @click="addTranslation">Add language</el-button>
          <p class="small text-muted mt-2 mb-0">Becomes the hreflang set of the page.</p>
        </card>

        <card title="Settings">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <span>Published</span>
            <el-switch v-model="form.active" active-color="#a3cc0e" inactive-color="#f96332" />
          </div>
          <div class="d-flex align-items-center justify-content-between mb-3">
            <span>Closed for Google</span>
            <el-switch v-model="form.noindex" active-color="#f96332" inactive-color="#a3cc0e" />
          </div>
          <div class="d-flex align-items-center justify-content-between">
            <span>Closed for AI (ChatGPT &amp; co)</span>
            <el-switch v-model="form.noai" active-color="#f96332" inactive-color="#a3cc0e" />
          </div>
          <p class="small text-muted mt-3 mb-0">
            Unpublished pages answer 404 on the site. "Closed for Google" adds
            <code>noindex</code>; "Closed for AI" asks GPTBot, Google-Extended, ClaudeBot and the
            rest to skip the page.
          </p>
        </card>

        <div class="mt-3">
          <el-button type="primary" :loading="saving" style="width: 100%" @click="save">
            {{ isEdit ? "Save" : "Create page" }}
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import KeyValueEditor from "@/components/CustomPages/KeyValueEditor.vue";

// language → the subdomain that serves it, so the editor can open the page it
// just wrote. Mirrors `mainPrefix` in the front's constants/languages.ts.
const LANGUAGE_HOSTS = {
  en: "us-uk",
  ru: "ru",
  ua: "ua",
  ar: "ar",
  id: "id",
  kz: "kz",
  es: "es",
  pt: "pt",
  de: "de",
  fr: "fr",
  pl: "pl",
  it: "it",
  jp: "jp",
};

const emptyHero = () => ({
  lead: "",
  updatedAt: "",
  disclosure: { label: "", text: "", url: "" },
  cta: { label: "", url: "" },
  authors: [],
});

let blockKeySeq = 0;
const nextBlockKey = () => {
  blockKeySeq += 1;
  return blockKeySeq;
};

const newContentBlock = () => ({
  _key: nextBlockKey(),
  heading: "",
  anchor: "",
  navTitle: "",
  content: "",
  // CKEditor 4 takes ownership of the config object it is handed — instances
  // sharing one object end up sharing state (typing in one overwrote another,
  // reported 2026-09-07). Every block gets its own fresh object for that reason.
  ckeditorConfig: { allowedContent: true },
});

const ANCHOR_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;

const emptyForm = () => ({
  lang: "en",
  alias: "",
  title: "",
  description: "",
  h1: "",
  // One entry per "html" block, in print order — no fixed head/body/footer
  // count, a page can have as many as it needs.
  contentBlocks: [],
  // Any block whose type isn't "html" or "hero" — edited as raw key/value
  // fields via KeyValueEditor so a future block type is never locked to
  // API-only edits just because this screen has no purpose-built form for it.
  customBlocks: [],
  hasHero: false,
  hero: emptyHero(),
  translations: [],
  imageAlt: "",
  active: true,
  noindex: false,
  noai: false,
});

export default {
  components: { KeyValueEditor },
  props: {
    mode: {
      type: String,
      default: "create",
    },
  },
  data() {
    return {
      editorRenderKey: 0,
      loading: false,
      saving: false,
      imageFile: null,
      imageFileDataUrl: null,
      storedImage: null,
      // The hero block's own heading/anchor/nav_title, preserved as loaded —
      // the hero card edits its `hero` payload, not this envelope.
      heroEnvelope: { heading: "", anchor: "", nav_title: "" },
      form: emptyForm(),
    };
  },
  computed: {
    ...mapState("cmsApiCatalogs", ["domains"]),
    isEdit() {
      return this.mode === "edit";
    },
    langs() {
      return (this.domains || []).map(({ language }) => language).filter(Boolean);
    },
    imagePreview() {
      return this.imageFileDataUrl || (this.storedImage && this.storedImage.main) || null;
    },
    publicUrl() {
      const host = LANGUAGE_HOSTS[this.form.lang];
      if (!host || !this.form.alias) return null;
      return `https://${host}.bookimed.com${this.form.alias}`;
    },
  },
  methods: {
    ...mapActions("customPages", ["getPage", "upsertPage", "uploadImage", "clearPage"]),
    ...mapActions("cmsApiCatalogs", ["getDomains"]),
    addTranslation() {
      this.form.translations.push({ lang: "", alias: "" });
    },
    removeTranslation(index) {
      this.form.translations.splice(index, 1);
    },
    addAuthor() {
      this.form.hero.authors.push({ kind: "author", name: "", role: "", avatarUrl: "", profileUrl: "" });
    },
    removeAuthor(index) {
      this.form.hero.authors.splice(index, 1);
    },
    addContentBlock() {
      this.form.contentBlocks.push(newContentBlock());
    },
    removeContentBlock(index) {
      this.form.contentBlocks.splice(index, 1);
    },
    moveContentBlock(index, delta) {
      const target = index + delta;
      if (target < 0 || target >= this.form.contentBlocks.length) {
        return;
      }
      const blocks = this.form.contentBlocks;
      const [moved] = blocks.splice(index, 1);
      blocks.splice(target, 0, moved);
    },
    removeCustomBlock(index) {
      this.form.customBlocks.splice(index, 1);
    },
    /** Maps the API's hero shape (snake_case, nullable sub-objects) onto the form's. */
    heroFromApi(hero) {
      const disclosure = hero.disclosure || {};
      const cta = hero.cta || {};
      return {
        lead: hero.lead || "",
        updatedAt: hero.updated_at || "",
        disclosure: {
          label: disclosure.label || "",
          text: disclosure.text || "",
          url: disclosure.url || "",
        },
        cta: {
          label: cta.label || "",
          url: cta.url || "",
        },
        authors: (hero.authors || []).map((author) => ({
          kind: author.kind || "author",
          name: author.name || "",
          role: author.role || "",
          avatarUrl: author.avatar_url || "",
          profileUrl: author.profile_url || "",
        })),
      };
    },
    contentBlockFromApi(block) {
      return {
        _key: nextBlockKey(),
        heading: block.heading || "",
        anchor: block.anchor || "",
        navTitle: block.nav_title || "",
        content: block.content || "",
        ckeditorConfig: { allowedContent: true },
      };
    },
    /** Any block type this screen has no dedicated form for — kept editable via KeyValueEditor. */
    customBlockFromApi(block) {
      const { type, heading, anchor, nav_title: navTitle, ...data } = block;
      return {
        _key: nextBlockKey(),
        type,
        heading: heading || "",
        anchor: anchor || "",
        navTitle: navTitle || "",
        data,
      };
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        this.$notify({ type: "warning", title: "Only .jpg and .png are allowed" });
        return;
      }
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        this.imageFileDataUrl = reader.result;
        this.imageFile = file;
      });
      reader.readAsDataURL(file);
    },
    normalizeBlocks(page) {
      const raw = page.blocks;
      if (Array.isArray(raw)) {
        return raw.map((block) => ({ ...block }));
      }
      if (!raw) {
        return [];
      }
      return ["head", "body", "footer"]
        .map((slot) => raw[slot])
        .filter((block) => block && block.content)
        .map((block) => ({ type: "html", content: block.content }));
    },
    fillFromPage(page) {
      const blocks = this.normalizeBlocks(page);
      const heroBlock = blocks.find((block) => block.type === "hero") || null;

      this.heroEnvelope = heroBlock
        ? {
          heading: heroBlock.heading || "",
          anchor: heroBlock.anchor || "",
          nav_title: heroBlock.nav_title || "",
        }
        : { heading: "", anchor: "", nav_title: "" };

      this.storedImage = page.image || null;
      this.form = {
        lang: page.lang,
        alias: page.alias,
        title: page.title || "",
        description: page.description || "",
        h1: page.h1 || "",
        hasHero: Boolean(heroBlock),
        hero: heroBlock ? this.heroFromApi(heroBlock.hero || {}) : emptyHero(),
        contentBlocks: blocks
          .filter((block) => block.type === "html")
          .map((block) => this.contentBlockFromApi(block)),
        customBlocks: blocks
          .filter((block) => block.type !== "html" && block.type !== "hero")
          .map((block) => this.customBlockFromApi(block)),
        translations: (page.alternates || []).map(({ lang, alias }) => ({ lang, alias })),
        imageAlt: (page.image && page.image.alt) || "",
        active: !page.hide,
        noindex: !!page.noindex,
        noai: !!page.noai,
      };
      this.editorRenderKey += 1;
    },
    /** Maps the form's hero fields back onto the API's snake_case shape. */
    buildHeroPayload() {
      const hero = this.form.hero;
      return {
        lead: hero.lead || "",
        updated_at: hero.updatedAt || "",
        disclosure: {
          label: hero.disclosure.label || "",
          text: hero.disclosure.text || "",
          url: hero.disclosure.url || "",
        },
        cta: {
          label: hero.cta.label || "",
          url: hero.cta.url || "",
        },
        authors: hero.authors
          .filter((author) => author.name && author.name.trim())
          .map((author) => ({
            kind: author.kind || "author",
            name: author.name.trim(),
            role: author.role || "",
            avatar_url: author.avatarUrl || "",
            profile_url: author.profileUrl || "",
          })),
      };
    },
    /**
     * seo rejects the whole save if an anchor doesn't match ANCHOR_PATTERN or
     * repeats across blocks (UpsertCustomPageHandler) — checked here first so
     * that shows up as a specific message instead of a raw error code.
     */
    validateAnchors() {
      const anchors = [
        this.form.hasHero ? this.heroEnvelope.anchor : "",
        ...this.form.contentBlocks.map((block) => block.anchor),
        ...this.form.customBlocks.map((block) => block.anchor),
      ];
      const seen = new Set();
      for (const raw of anchors) {
        const anchor = (raw || "").trim();
        if (!anchor) {
          continue;
        }
        if (!ANCHOR_PATTERN.test(anchor)) {
          return `Anchor "${anchor}" is invalid — lowercase letters, digits and hyphens only, starting with a letter or digit.`;
        }
        if (seen.has(anchor)) {
          return `Anchor "${anchor}" is used more than once — anchors must be unique on the page.`;
        }
        seen.add(anchor);
      }
      return null;
    },
    buildBlocks() {
      const blocks = [];

      // Hero must be block 0 or the seo service rejects the save outright
      // (UpsertCustomPageHandler: blocks.hero_not_first) — always emitted
      // first, never mixed in with content/custom blocks below.
      if (this.form.hasHero) {
        blocks.push({ ...this.heroEnvelope, type: "hero", hero: this.buildHeroPayload() });
      }

      this.form.contentBlocks.forEach((block) => {
        blocks.push({
          type: "html",
          heading: block.heading || "",
          anchor: block.anchor || "",
          nav_title: block.navTitle || "",
          content: block.content || "",
        });
      });

      this.form.customBlocks.forEach((block) => {
        blocks.push({
          type: block.type,
          heading: block.heading || "",
          anchor: block.anchor || "",
          nav_title: block.navTitle || "",
          ...block.data,
        });
      });

      // An empty html block is dropped rather than saved as a blank section;
      // hero and custom blocks have no uniform "empty" concept, so they are
      // always kept once present in the form.
      return blocks.filter((block) => "html" !== block.type || "" !== (block.content || "").trim());
    },
    buildPayload() {
      return {
        lang: this.form.lang,
        alias: this.form.alias,
        title: this.form.title,
        description: this.form.description,
        h1: this.form.h1,
        blocks: this.buildBlocks(),
        translations: this.form.translations.filter(({ lang, alias }) => lang && alias),
        hide: !this.form.active,
        noindex: this.form.noindex,
        noai: this.form.noai,
      };
    },
    async save() {
      if (!this.form.lang || !this.form.alias) {
        this.$notify({ type: "warning", title: "Language and URL are required" });
        return;
      }
      const anchorError = this.validateAnchors();
      if (anchorError) {
        this.$notify({ type: "warning", title: anchorError });
        return;
      }
      this.saving = true;
      try {
        const response = await this.upsertPage(this.buildPayload());
        const saved = (response && response.data && response.data.page) || null;
        if (saved) {
          this.storedImage = saved.image || null;
        }

        if (this.imageFile) {
          const uploaded = await this.uploadImage({
            lang: this.form.lang,
            alias: (saved && saved.alias) || this.form.alias,
            file: this.imageFile,
            alt: this.form.imageAlt,
          });
          const page = uploaded && uploaded.data && uploaded.data.page;
          if (page) {
            this.storedImage = page.image || null;
          }
          this.imageFile = null;
          this.imageFileDataUrl = null;
        }

        this.$notify({ type: "success", title: this.isEdit ? "Page saved" : "Page created" });

        if (!this.isEdit && saved) {
          this.$router.push({
            name: "custom-pages-edit",
            query: { lang: saved.lang, url: saved.alias },
          });
        }
      } catch (e) {
        const code = e && e.response && e.response.data && e.response.data.error && e.response.data.error.code;
        this.$notify({ type: "warning", title: code || "Error saving the page" });
      } finally {
        this.saving = false;
      }
    },
    async load() {
      const { lang, url } = this.$route.query;
      if (!lang || !url) {
        this.$notify({ type: "warning", title: "No page address in the link" });
        return;
      }
      this.loading = true;
      try {
        const page = await this.getPage({ lang, alias: url });
        if (page) {
          this.fillFromPage(page);
        }
      } catch (e) {
        this.$notify({ type: "warning", title: "Page not found" });
        this.$router.push({ name: "custom-pages-list" });
      } finally {
        this.loading = false;
      }
    },
  },
  async created() {
    this.clearPage();
    await this.getDomains();
    if (this.isEdit) {
      await this.load();
      return;
    }
    if (this.$route.query.lang) {
      this.form.lang = this.$route.query.lang;
    }
    if (this.langs.length && !this.langs.includes(this.form.lang)) {
      this.form.lang = this.langs[0];
    }
  },
};
</script>

<style scoped>
.custom-page-form__layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.custom-page-form__main {
  flex: 1;
  min-width: 0;
}
.custom-page-form__sidebar {
  width: 320px;
  flex-shrink: 0;
}
.custom-page-form__img {
  max-width: 100%;
  max-height: 140px;
  border-radius: 8px;
  object-fit: contain;
}
.custom-page-form__upload-btn {
  display: inline-block;
  padding: 8px 20px;
  background: #f4f8fb;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
}
.custom-page-form__upload-btn:hover {
  background: #e9ecef;
}
.hero-author-row {
  border-bottom: 1px solid #ebeef5;
}
.hero-author-row:last-of-type {
  border-bottom: none;
}
.content-block-item {
  border-bottom: 1px solid #ebeef5;
}
.content-block-item:last-of-type {
  border-bottom: none;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
</style>
